import { z } from "zod";

/**
 * Response for `PATCH /v4/property/{property}/activation` — either a
 * no-op (already in the requested state, `activation_id: null`) or a
 * freshly queued job (`activation_id` set, poll the status endpoint).
 */
export const ToggleActiveResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    data: z.object({
        activation_id: z.string().nullable(),
        property_id: z.string(),
        direction: z.enum(['activate', 'deactivate']).optional(),
        status: z.string().optional(),
        is_property_active: z.boolean().optional(),
        // Users un-assigned as part of a deactivate (absent on older backends).
        unassigned_user_ids: z.array(z.string()).optional(),
    }),
});

/** Response for `GET /v4/property/{property}/activation/users`. */
export const ActivationUsersResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    data: z.array(z.object({
        id: z.string(),
        name: z.string(),
        email: z.string(),
        // Platform accounts that can never be un-assigned automatically.
        is_protected: z.boolean(),
    })),
});

const ActivationStepSchema = z.object({
    key: z.string(),
    label: z.string(),
    status: z.enum(['pending', 'running', 'done', 'done_with_errors', 'skipped']),
    total: z.number(),
    processed: z.number(),
    failed: z.number(),
});

const ActivationErrorSchema = z.object({
    type: z.string(),
    local_id: z.string().nullable(),
    name: z.string().nullable(),
    message: z.string(),
});

/**
 * Response for `GET /v4/property/{property}/activation/{activation}/status`.
 * Polled by `usePropertyActivationStatus` until `status` reaches a terminal
 * value (completed / completed_with_errors / failed).
 */
export const ActivationStatusResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    data: z.object({
        status: z.enum(['queued', 'processing', 'completed', 'completed_with_errors', 'failed']),
        direction: z.enum(['activate', 'deactivate']),
        current_step: z.string().nullable(),
        total: z.number(),
        processed: z.number(),
        failed: z.number(),
        steps: z.array(ActivationStepSchema),
        errors: z.array(ActivationErrorSchema),
        error_message: z.string().nullable(),
    }),
});

export type ActivationUsersResponse = z.infer<typeof ActivationUsersResponseSchema>;
export type ToggleActiveResponse = z.infer<typeof ToggleActiveResponseSchema>;
export type ActivationStep = z.infer<typeof ActivationStepSchema>;
export type ActivationError = z.infer<typeof ActivationErrorSchema>;
export type ActivationStatusResponse = z.infer<typeof ActivationStatusResponseSchema>;
